import { useLevelReducer } from './useLevelReducer';
import { useEffect, useRef, useState } from 'react';
import { Level } from '../../types/level';
import WordService, { WordStats } from '../../api/wordService';
import { useParams } from 'react-router';
import useAppContext from '../../hooks/useAppContext';
import {
  CanvasLevelRenderComponent,
  GeneratorComponent,
  KeywordComponent,
  WordsComponent,
} from '../../controls';
import { getHash } from '../../types/levelUtils';
import { Word } from '../../types/word';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { ButtonGroup } from 'primereact/buttongroup';
import React from 'react';
import useMenu from '../../routes/routes';
import LevelService from '../../api/levelService';
import toast from 'react-hot-toast';

type ManageAction = 'close' | 'save';

const EditorPage = () => {
  const { id } = useParams();
  const { goToMaps } = useMenu();
  const search = new URLSearchParams(window.location.search);
  const parsedTargetNumber = Number.parseInt(search.get('number') || '1', 10);
  const targetNumber = Number.isNaN(parsedTargetNumber) ? 1 : Math.max(1, parsedTargetNumber);

  const [levelKeyword, setLevelKeyword] = useState<string>('');
  const [heap, setHeap] = useState<string[]>([]);
  const [variants, setVariants] = useState<Level[]>([]);
  const [stats, setStats] = useState<WordStats[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [levelNumber, setLevelNumber] = useState<number>(targetNumber);

  const init = useRef<boolean>(false);
  const lastGenHash = useRef<string>('');
  const { Lang } = useAppContext();

  const { level, onSelect, onToggleLock, onFlip, onMove, onApply } = useLevelReducer();

  const saveLevel = async (action: ManageAction, level: Level) => {
    if (action !== 'save') {
      goToMaps();
      return;
    }

    setLoading(true);
    try {
      level.keyword = levelKeyword;
      level.number = levelNumber;
      const saved = await LevelService.saveLevel(level);
      if (saved) {
        goToMaps();
      } else {
        toast.error('Failed to save level');
      }
    } catch {
      toast.error('Failed to save level');
    } finally {
      setLoading(false);
    }
  };

  const loadLevel = async (id: number) => {
    if (loading) return;

    setLoading(true);
    try {
      const loadedLevel = await LevelService.getLevel(id);
      if (!loadedLevel) {
        goToMaps();
        return;
      }

      const data = await WordService.getHeap(Lang, loadedLevel.keyword);
      onApply(loadedLevel);
      setLevelNumber(loadedLevel.number);
      setLevelKeyword(loadedLevel.keyword);
      setHeap([...data.variants]);
      setStats([...data.stats]);
    } catch {
      toast.error('Failed to load level');
    } finally {
      setLoading(false);
    }
  };

  const getNewLevel = async (newKeyword: string = '') => {
    if (loading) return;

    setLoading(true);
    try {
      const levelNumber = level.number || targetNumber;
      const data = await LevelService.getFullLevel(Lang, newKeyword, levelNumber);

      let newLevel: Level;
      if (data.levels.length > 0) {
        newLevel = data.levels[0];
        newLevel.id = level.id;
        newLevel.number = levelNumber;
        newKeyword = newLevel.keyword;
      } else {
        newKeyword = data.heap.main;

        const word = new Word(data.heap.variants[0]);
        word.metadata.isLocked = true;

        newLevel = new Level(Lang, [word]);
        newLevel.id = level.id;
        newLevel.number = levelNumber;
        newLevel.keyword = newKeyword;
      }

      lastGenHash.current = getHash(newLevel);

      onApply(newLevel);

      setLevelKeyword(newKeyword);
      setHeap([...data.heap.variants]);
      setVariants([...data.levels]);
      setStats([...data.heap.stats]);
    } catch {
      toast.error('Failed to generate level');
    } finally {
      setLoading(false);
    }
  };

  const onDictEntryDelete = async (word: string) => {
    if (window.confirm(`Do you really want to delete '${word}?'`)) {
      setLoading(true);
      try {
        const result = await WordService.deleteDict(Lang, word);
        if (!result) {
          toast.error('Failed to delete word');
          return;
        }

        setHeap([...heap.filter((h) => h !== word)]);
        onSelect(word, false);
      } catch {
        toast.error('Failed to delete word');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (init.current) return;
    init.current = true;

    if (!id) {
      getNewLevel();
    } else {
      const parsedId = Number.parseInt(id, 10);
      if (!Number.isNaN(parsedId)) {
        loadLevel(parsedId);
      } else {
        toast.error('Failed to parse level id');
        goToMaps();
      }
    }
  });

  useEffect(() => {
    if (!init.current) return;

    const loadVariants = async () => {
      setLoading(true);
      try {
        const data = await LevelService.rebuildLevel(level);
        setVariants(data);
      } catch {
        toast.error('Failed to rebuild level');
      } finally {
        setLoading(false);
      }
    };

    if (level.keyword && lastGenHash.current !== getHash(level)) {
      lastGenHash.current = getHash(level);
      loadVariants();
    }
  }, [level]);

  const onGeneratorSelect = (lvl: Level) => {
    lastGenHash.current = getHash(lvl);
    onApply(lvl);
  };

  return (
    <div className="page-editor">
      <Toolbar
        className="mb-1"
        start={
          <div className="flex align-items-center gap-2">
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              size="small"
              text
              onClick={() => goToMaps(levelNumber)}
            />
            <span className="font-bold">#{levelNumber}</span>
          </div>
        }
        end={
          <ButtonGroup>
            <Button label="Close" size="small" onClick={() => saveLevel('close', level)} />
            <Button label="Save" size="small" onClick={() => saveLevel('save', level)} />
          </ButtonGroup>
        }
      />

      <div className="flex flex-row gap-1 justify-content-center">
        <div className="flex flex-column gap-1">
          <KeywordComponent enabled={!loading} keyword={levelKeyword} onChange={getNewLevel} />
          <CanvasLevelRenderComponent enabled={!loading} onMove={onMove} level={level} />
          <GeneratorComponent
            loading={loading}
            selected={lastGenHash.current}
            levels={variants}
            onSelected={onGeneratorSelect}
          />
        </div>

        <WordsComponent
          enabled={!loading}
          words={heap}
          locked={level.words.filter((w) => w.metadata.isLocked).map((w) => w.word)}
          selected={level.words.map((w) => w.word)}
          stats={stats}
          onToggleLock={onToggleLock}
          onSelect={onSelect}
          onFlip={onFlip}
          onDelete={onDictEntryDelete}
        />
      </div>
    </div>
  );
};

export default EditorPage;
